import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Note, TimeEntry, TimeFixRequest } from '@shared/types'
import { get } from '../lib/api'
import {
  formatDate, formatDateTime, formatDuration, formatTime, localDateKey, shiftDateKey, toDecimalHours,
} from '../lib/format'
import { useToday } from '../state/today'
import { useToast } from '../state/toast'
import { RequestFixSheet } from '../components/RequestFixSheet'
import { NoteCard } from '../components/NoteCard'
import { Button, EmptyState, SectionHeading, Spinner } from '../components/ui'

type Tab = 'updates' | 'hours'

export default function LogPage() {
  const { state } = useToday()
  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>(() => (params.get('tab') === 'hours' ? 'hours' : 'updates'))
  const timezone = state?.timezone ?? 'UTC'
  const [date, setDate] = useState(() => localDateKey(new Date(), timezone))
  const siteId = params.get('siteId') ?? ''
  const type = params.get('type') ?? ''
  const filtered = tab === 'updates' && Boolean(siteId || type)

  useEffect(() => {
    if (state) setDate(localDateKey(new Date(state.now), state.timezone))
  }, [state?.timezone]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!state) return <div className="py-16"><Spinner /></div>

  const today = localDateKey(new Date(state.now), timezone)

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  return (
    <div className="space-y-5">
      <header>
        <p className="label">Your record</p>
        <h1 className="mt-0.5 text-2xl">Log</h1>
      </header>

      <div className="flex gap-1 border-b border-line">
        {(['updates', 'hours'] as Tab[]).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={`-mb-px flex min-h-12 items-center border-b-3 px-3 text-sm font-bold capitalize ${
              tab === value
                ? 'border-brand text-graphite'
                : 'border-transparent text-graphite-2'
            }`}
          >
            {value}
          </button>
        ))}
      </div>

      {tab === 'updates' && (
        <div className="grid grid-cols-2 gap-2">
          <select
            className="field"
            value={siteId}
            onChange={(e) => setFilter('siteId', e.target.value)}
            aria-label="Filter by site"
          >
            <option value="">All sites</option>
            {state.sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
          </select>
          <select
            className="field"
            value={type}
            onChange={(e) => setFilter('type', e.target.value)}
            aria-label="Filter by type"
          >
            <option value="">All types</option>
            {state.noteTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
      )}

      {/* A filter answers "show me every change order at Riverbend", which is
          a list across days, not one day at a time. */}
      {!filtered && (
        <div className="flex items-center justify-between gap-2">
          <Button className="min-h-10 px-3" onClick={() => setDate(shiftDateKey(date, -1))}>
            ‹ Prev
          </Button>
          <p className="font-mono text-sm font-semibold tnum">
            {date === today ? 'Today' : formatDate(`${date}T12:00:00Z`, 'UTC')}
          </p>
          <Button
            className="min-h-10 px-3"
            disabled={date >= today}
            onClick={() => setDate(shiftDateKey(date, 1))}
          >
            Next ›
          </Button>
        </div>
      )}

      {tab === 'updates'
        ? filtered
          ? <FilteredFeed siteId={siteId} type={type} timezone={timezone} />
          : <UpdatesTab date={date} timezone={timezone} />
        : <HoursTab date={date} timezone={timezone} />}
    </div>
  )
}

/**
 * The Log is your own updates plus the types the whole crew shares — change
 * orders and specs — whoever posted them. Somebody else's update shows their
 * name, and only your own can be moved to a different site.
 */
function LogNoteCard({ note, timezone, onChanged }: {
  note: Note
  timezone: string
  onChanged: (note: Note) => void
}) {
  const { state } = useToday()
  const mine = note.userId === state?.user.id
  return (
    <NoteCard
      note={note}
      noteTypes={state?.noteTypes ?? []}
      timezone={timezone}
      showAuthor={!mine}
      sites={mine ? state?.sites : undefined}
      onSiteChanged={mine ? onChanged : undefined}
    />
  )
}

function UpdatesTab({ date, timezone }: { date: string; timezone: string }) {
  const [notes, setNotes] = useState<Note[] | null>(null)

  useEffect(() => {
    let cancelled = false
    setNotes(null)
    const from = new Date(`${date}T00:00:00Z`)
    // Query a generous window and filter locally; the server stores UTC and the
    // day boundary depends on the org timezone.
    const params = new URLSearchParams({
      scope: 'log',
      from: new Date(from.getTime() - 86_400_000).toISOString(),
      to: new Date(from.getTime() + 2 * 86_400_000).toISOString(),
      limit: '100',
    })
    void get<{ notes: Note[] }>(`/api/notes?${params}`)
      .then((res) => {
        if (cancelled) return
        setNotes(res.notes.filter((n) => localDateKeyOf(n.occurredAt, timezone) === date))
      })
      .catch(() => !cancelled && setNotes([]))
    return () => {
      cancelled = true
    }
  }, [date, timezone])

  if (!notes) return <div className="py-10"><Spinner /></div>
  if (notes.length === 0) {
    return <EmptyState title="Nothing logged" body="No updates were posted on this day." />
  }

  return (
    <div className="space-y-3">
      {notes.map((note) => (
        <LogNoteCard
          key={note.id}
          note={note}
          timezone={timezone}
          onChanged={(updated) =>
            setNotes((current) => current?.map((n) => (n.id === updated.id ? updated : n)) ?? null)}
        />
      ))}
    </div>
  )
}

function FilteredFeed({ siteId, type, timezone }: { siteId: string; type: string; timezone: string }) {
  const [notes, setNotes] = useState<Note[] | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  const query = useCallback((next?: string | null) => {
    const params = new URLSearchParams({ scope: 'log', limit: '30' })
    if (siteId) params.set('siteId', siteId)
    if (type) params.set('type', type)
    if (next) params.set('cursor', next)
    return `/api/notes?${params}`
  }, [siteId, type])

  useEffect(() => {
    let cancelled = false
    setNotes(null)
    void get<{ notes: Note[]; nextCursor: string | null }>(query())
      .then((res) => {
        if (cancelled) return
        setNotes(res.notes)
        setCursor(res.nextCursor)
      })
      .catch(() => !cancelled && setNotes([]))
    return () => {
      cancelled = true
    }
  }, [query])

  const loadMore = async () => {
    if (!cursor) return
    setLoadingMore(true)
    try {
      const res = await get<{ notes: Note[]; nextCursor: string | null }>(query(cursor))
      setNotes((current) => [...(current ?? []), ...res.notes])
      setCursor(res.nextCursor)
    } finally {
      setLoadingMore(false)
    }
  }

  if (!notes) return <div className="py-10"><Spinner /></div>
  if (notes.length === 0) {
    return <EmptyState title="Nothing here" body="No updates match these filters." />
  }

  // Day headings, since a filtered list runs across days.
  let lastDay = ''
  return (
    <div className="space-y-3">
      {notes.map((note) => {
        const day = localDateKeyOf(note.occurredAt, timezone)
        const heading = day !== lastDay
        lastDay = day
        return (
          <div key={note.id} className="space-y-3">
            {heading && <SectionHeading>{formatDate(note.occurredAt, timezone)}</SectionHeading>}
            <LogNoteCard
              note={note}
              timezone={timezone}
              onChanged={(updated) =>
                setNotes((current) => current?.map((n) => (n.id === updated.id ? updated : n)) ?? null)}
            />
          </div>
        )
      })}
      {cursor && (
        <Button className="w-full" loading={loadingMore} onClick={() => void loadMore()}>
          Load older
        </Button>
      )}
    </div>
  )
}

const STATUS_LABEL: Record<TimeFixRequest['status'], string> = {
  pending: 'Waiting on the office',
  approved: 'Fixed',
  declined: 'Declined',
}

function HoursTab({ date, timezone }: { date: string; timezone: string }) {
  const { state, refresh } = useToday()
  const toast = useToast()
  const [entries, setEntries] = useState<TimeEntry[] | null>(null)
  const [requests, setRequests] = useState<TimeFixRequest[]>([])
  /** undefined = closed; null = a missing shift. */
  const [fixing, setFixing] = useState<TimeEntry | null | undefined>(undefined)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setEntries(null)
    void get<{ entries: TimeEntry[] }>(`/api/time/entries?date=${date}&days=1`)
      .then((res) => !cancelled && setEntries(res.entries))
      .catch(() => !cancelled && setEntries([]))
    void get<{ requests: TimeFixRequest[] }>('/api/time/fix-requests')
      .then((res) => !cancelled && setRequests(res.requests))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [date, reloadKey])

  if (!entries) return <div className="py-10"><Spinner /></div>

  const total = entries.reduce((sum, entry) => sum + entry.durationMs, 0)
  const pendingFor = (entryId: string) =>
    requests.some((r) => r.entryId === entryId && r.status === 'pending')

  return (
    <div className="space-y-3">
      {requests.length > 0 && (
        <>
          <SectionHeading>Fix requests</SectionHeading>
          {requests.slice(0, 5).map((request) => (
            <div
              key={request.id}
              className={`card p-3 ${request.status === 'pending' ? 'border-l-4 border-l-brand' : ''}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <p className="truncate font-bold">{request.siteName}</p>
                <span
                  className={`shrink-0 font-mono text-[10px] font-semibold tracking-wider uppercase ${
                    request.status === 'approved' ? 'text-ok'
                      : request.status === 'declined' ? 'text-alarm' : 'text-graphite-2'
                  }`}
                >
                  {STATUS_LABEL[request.status]}
                </span>
              </div>
              <p className="font-mono text-xs text-graphite-2 tnum">
                {formatDateTime(request.clockInAt, timezone)} –{' '}
                {request.clockOutAt ? formatTime(request.clockOutAt, timezone) : 'still on'}
              </p>
              <p className="mt-0.5 text-xs text-graphite-2">{request.reason}</p>
              {request.reviewComment && (
                <p className="mt-1 text-xs">
                  <span className="font-bold">{request.reviewedByName ?? 'Office'}:</span>{' '}
                  “{request.reviewComment}”
                </p>
              )}
            </div>
          ))}
        </>
      )}

      <SectionHeading
        action={
          <button type="button" className="link text-xs" onClick={() => setFixing(null)}>
            Missing a shift?
          </button>
        }
      >
        Entries
      </SectionHeading>
      {entries.length === 0 ? (
        <EmptyState title="No hours" body="You were not clocked in on this day." />
      ) : entries.map((entry) => (
        <div key={entry.id} className="card flex items-center justify-between gap-3 p-3">
          <div className="min-w-0">
            <p className="truncate font-bold">{entry.siteName}</p>
            <p className="font-mono text-xs text-graphite-2 tnum">
              {formatTime(entry.clockInAt, timezone)} –{' '}
              {entry.clockOutAt ? formatTime(entry.clockOutAt, timezone) : 'now'}
              {entry.source === 'admin' && ' · edited by office'}
            </p>
            {entry.editReason && (
              <p className="mt-0.5 text-xs text-graphite-2">{entry.editReason}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <p className="font-mono text-lg font-semibold tnum">
              {formatDuration(entry.durationMs)}
            </p>
            {pendingFor(entry.id) ? (
              <span className="chip text-graphite-2" style={{ boxShadow: 'inset 0 0 0 1px var(--wm-line-strong)' }}>
                sent
              </span>
            ) : (
              <Button className="min-h-10 px-3 text-xs" onClick={() => setFixing(entry)}>
                Fix
              </Button>
            )}
          </div>
        </div>
      ))}
      {fixing !== undefined && state && (
        <RequestFixSheet
          entry={fixing}
          date={date}
          sites={state.sites}
          timezone={timezone}
          onClose={() => setFixing(undefined)}
          onSent={async (message) => {
            setFixing(undefined)
            toast.success(message)
            setReloadKey((k) => k + 1)
            await refresh()
          }}
        />
      )}
      {entries.length > 0 && (
        <div className="flex items-center justify-between border-t-2 border-graphite pt-3">
          <span className="label">Day total</span>
          <span className="font-mono text-xl font-semibold tnum">
            {formatDuration(total)} · {toDecimalHours(total)} h
          </span>
        </div>
      )}
    </div>
  )
}

function localDateKeyOf(iso: string, timezone: string): string {
  return localDateKey(new Date(iso), timezone)
}
